import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import InputMask from 'react-input-mask'
import DOMPurify from 'dompurify'

import AlertModal from '@components/AlertModal'
import { getActiveNotification } from '@services/sm-api.js'
import { openModal } from '@actions/modal'
import { clearVoucher, requestVoucher } from '@actions/preVoucher'
import { clearSpecialDiscount } from '@actions/specialDiscount'
import { fetchSchoolPaymentOptions } from '@actions/school'
import { insertStudent, removeStudent } from '@actions/checkout'
import { updateActiveVoucher } from '@actions/activeVoucher.js'
import { validateStudent } from '@shared/studentValidation'

import V2Layout from '../V2Layout'
import carritoNeutro from '../assets/carrito-neutro.png'
import carritoConfuso from '../assets/carrito-confuso.png'
import logoSmBranco from '../assets/sm-educacao-horizontal-branco.png'
import faqCategories from '@components/FaqList/questions'

// Home do fluxo V2 — porte fiel do design "Nova home SM Educação" (Claude Design).
// CAMADA VISUAL nova; a lógica de negócio é 100% reusada do V1 (mesmas actions,
// reducers e validação compartilhada). Regras: V-05/06/08..V-14, V-18..V-26, V-28.

const label = { display: 'block', margin: '26px 0 8px', fontSize: '12px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#6B6664' }
const inputBase = { width: '100%', height: '54px', padding: '0 16px', border: '1.5px solid #E3DBD7', borderRadius: '12px', fontSize: '16px', color: '#373535', outline: 'none' }

// FONTE ÚNICA: as mesmas perguntas/respostas do FAQ do V1 (FaqList/questions.js) —
// mudanças feitas pelo time lá refletem aqui automaticamente.
const FAQ_ITEMS = faqCategories
  .flatMap(cat => (cat.items || []).map(item => ({ category: cat.category, q: item.question, a: item.answer, highlight: !!item.highlight })))
  .filter(item => item.q && item.a)
// destaques da home marcados no próprio FAQ (`highlight`), sem depender da ordem do arquivo
const FAQ_HIGHLIGHTS = FAQ_ITEMS.some(item => item.highlight) ? FAQ_ITEMS.filter(item => item.highlight) : FAQ_ITEMS.slice(0, 5)
const FAQ_PREVIEW = FAQ_HIGHLIGHTS.length

const openAssistant = detail => window.dispatchEvent(new CustomEvent('smd:assistant', { detail: detail || {} }))

const Tick = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D8262C" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
)
const TICKS = ['Lista definida pela escola', 'Compra 100% online', 'Pagamento seguro']

export default function V2Home() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const preVoucher = useSelector(state => state.preVoucher)
  const students = useSelector(state => state.checkout)

  const [voucher, setVoucher] = useState('')
  const [name, setName] = useState('')
  const [birthdate, setBirthdate] = useState('')
  const [step2Hint, setStep2Hint] = useState('')
  const [openFaq, setOpenFaq] = useState(0)
  const [allFaq, setAllFaq] = useState(false)
  const activeNotification = useSelector(state => state.activeNotification)

  const voucherIsValid = preVoucher.voucher && preVoucher.error && preVoucher.error.code === null

  // Notificações administradas — mesmas regras do V1 (MainMenu/Hero/Voucher),
  // que na V2 ficavam mudas porque o chrome V1 está oculto pelo ChromeGate:
  // 1) pop-up geral, só na página inicial e UMA vez por navegador (localStorage);
  useEffect(() => {
    if (activeNotification && !localStorage.getItem('notificationIsShowed')) {
      localStorage.setItem('notificationIsShowed', 'showed')
      dispatch(openModal(<AlertModal notification={activeNotification} />))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeNotification])

  // 2) V-15: notificação vinculada ao voucher, exibida ao avançar para o passo do aluno
  useEffect(() => {
    if (voucherIsValid && preVoucher.voucher) {
      getActiveNotification(preVoucher.voucher).then(n => {
        if (n) dispatch(openModal(<AlertModal notification={n} />))
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voucherIsValid, preVoucher.voucher])

  // 3) block_checkout: bloqueia a inserção de voucher (o V1 troca o form pelo FAQ)
  const checkoutBlocked = !!(activeNotification && activeNotification.block_checkout)

  const _handleNext = () => {
    // V-05: limpa persistência e desconto especial antes de consultar
    localStorage.removeItem('reduxPersist:checkout')
    localStorage.removeItem('reduxPersist:preVoucher')
    dispatch(clearSpecialDiscount())
    dispatch(requestVoucher(voucher)).then(() => {
      window.ga && window.ga('send', { hitType: 'event', eventCategory: 'compra', eventAction: 'click', eventLabel: 'inseriu_voucher_1' })
    })
  }

  const _resetVoucher = () => {
    dispatch(clearVoucher())
    setVoucher('')
    setStep2Hint('')
  }

  const _handleBuy = () => {
    // mesma validação do V1 (V-18..V-25, compartilhada)
    const { valid, errorMessage } = validateStudent(name, birthdate)

    // V-26: a home sempre inicia carrinho novo
    students.forEach((s, index) => dispatch(removeStudent(index)))

    if (!valid) {
      setStep2Hint(errorMessage)
      return
    }

    window.ga && window.ga('send', { hitType: 'event', eventCategory: 'compra', eventAction: 'click', eventLabel: 'inseriu_nome_aluno_2' })
    dispatch(fetchSchoolPaymentOptions(preVoucher.school_sap_code))
    dispatch(insertStudent({ ...preVoucher, name, birthdate }))
    dispatch(updateActiveVoucher(preVoucher.voucher, name))
    dispatch(clearVoucher())
    sessionStorage.setItem('smd_v2_flow', '1') // marca o fluxo V2 para as pontes de navegação
    navigate('/v2/steps/selecionar-livros')
  }

  // mensagens de erro do voucher: mesmos códigos e textos do V1 (V-08..V-14)
  const error = preVoucher.error || {}
  let voucherHint = ''
  if (!voucherIsValid) {
    if (error.code === 2) voucherHint = `Voucher expirado em ${preVoucher.contract_end}`
    else if (error.code === 6) voucherHint = 'Este voucher está inativo, pois o prazo de compras foi encerrado.'
    else if (error.code !== null && error.code !== undefined && error.message) voucherHint = error.message
  }

  const faqShown = allFaq ? FAQ_ITEMS : FAQ_HIGHLIGHTS

  return (
    <V2Layout>
      {/* HERO — guia de identidade SM Direto (set/2026): neutro quente, Carrito ao lado do cartão do voucher,
          apoiado na mesma base, que é o único cartão com borda vermelha da tela */}
      <section className="v2-hero2">
        <div className="v2-hero-grid v2-hero2__in">
          <div className="v2-hero2__copy">
            <span className="v2-pill-eyebrow">Compra com voucher da escola</span>
            <h1>O material da escola, em poucos cliques.</h1>
            <p>Use o número do voucher enviado pela escola para conferir a lista de produtos e concluir a compra em três etapas.</p>
            <ul className="v2-ticks">
              {TICKS.map(t => (
                <li key={t}><Tick />{t}</li>
              ))}
            </ul>
          </div>

          <div className={`v2-hero2__cardwrap${voucherHint ? ' has-error' : ''}`}>
            {/* pedido de 18/09: Carrito neutro INTEIRO ao lado do cartão (antes ficava meio escondido atrás dele) */}
            <img className="v2-hero2__carrito" src={carritoNeutro} alt="" aria-hidden="true" />
            <div className="v2-voucher-card">
            {checkoutBlocked ? (
              /* block_checkout: sem inserção de voucher (regra do Hero V1); a própria
                 notificação explica o motivo, com atalho para o FAQ como no V1 */
              <div id="v2-checkout-blocked">
                <h2 style={{ margin: 0, fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '24px', lineHeight: '1.25', color: '#373535' }}>
                  {activeNotification.title}
                </h2>
                <div
                  style={{ margin: '12px 0 0', fontSize: '15px', lineHeight: '1.6', color: '#6B6664' }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(activeNotification.message) }}
                />
                <a href="#ajuda" style={{ display: 'inline-block', marginTop: '18px', fontSize: '14px', fontWeight: '700', color: '#D8262C', textDecoration: 'none' }}>
                  Ver dúvidas frequentes →
                </a>
              </div>
            ) : !voucherIsValid ? (
              <div>
                <h2 style={{ margin: 0, fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '24px', lineHeight: '1.25', color: '#373535' }}>Comece pelo voucher</h2>
                <p style={{ margin: '10px 0 0', fontSize: '15px', lineHeight: '1.55', color: '#6B6664' }}>Você encontra o número na comunicação enviada pela escola.</p>
                <label htmlFor="v2-voucher-input" style={label}>Número do voucher</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    id="v2-voucher-input"
                    className="v2d-input"
                    type="text"
                    value={voucher}
                    onChange={e => setVoucher(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && _handleNext()}
                    placeholder="Ex.: 1211F112"
                    autoComplete="off"
                    spellCheck="false"
                    style={{ ...inputBase, flex: 1, minWidth: 0, width: 'auto', fontSize: '18px', fontWeight: '600', letterSpacing: '0.14em', textTransform: 'uppercase' }}
                  />
                  <button
                    id="v2-voucher-next"
                    type="button"
                    onClick={_handleNext}
                    style={{ height: '54px', padding: '0 26px', border: 'none', borderRadius: '12px', background: '#D8262C', color: '#fff', fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '15px', cursor: 'pointer' }}
                  >
                    Próximo
                  </button>
                </div>
                {voucherHint ? (
                  <div className="v2-vhint">
                    <img src={carritoConfuso} alt="" aria-hidden="true" />
                    <div>
                      <strong>Não conseguimos validar esse voucher.</strong>
                      <p id="v2-voucher-hint">{voucherHint}</p>
                      <span>Confira o número na comunicação da escola ou fale com o atendimento.</span>
                    </div>
                  </div>
                ) : (
                  <p id="v2-voucher-hint" style={{ margin: '14px 0 0', fontSize: '13px', lineHeight: '1.5', color: '#8E8783' }} />
                )}
                <div style={{ marginTop: '26px', paddingTop: '22px', borderTop: '1px solid #EFE7E3', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                  <span style={{ fontSize: '14px', color: '#6B6664' }}>Não recebeu o voucher?</span>
                  <button
                    type="button"
                    id="v2-voucher-help"
                    onClick={() => window.dispatchEvent(new CustomEvent('smd:assistant', { detail: { intent: 'voucher_sem', label: 'Não recebi o voucher' } }))}
                    style={{ border: 'none', background: 'none', padding: 0, fontSize: '14px', fontWeight: '700', color: '#D8262C', cursor: 'pointer' }}
                  >
                    Falar com a gente →
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 12px', borderRadius: '100px', background: '#FDEAEA', fontSize: '12px', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#D8262C' }}>
                    Voucher {preVoucher.voucher}
                  </span>
                  <button id="v2-voucher-reset" type="button" onClick={_resetVoucher} style={{ border: 'none', background: 'none', padding: 0, fontSize: '13px', fontWeight: '700', color: '#6B6664', cursor: 'pointer' }}>
                    Trocar voucher
                  </button>
                </div>
                {/* pedido de negócio (09/09, item 1): série em destaque logo abaixo do voucher,
                    para o responsável perceber um voucher errado antes de comprar */}
                <div id="v2-voucher-grade" className="v2-vgrade" style={{ marginTop: '16px', padding: '14px 16px', borderRadius: '12px', background: '#FDEAEA', border: '1px solid #F3C9CB', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                  <div style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: '11px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#C2262B' }}>Série do voucher</span>
                    <span style={{ display: 'block', marginTop: '3px', fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '18px', lineHeight: '1.25', color: '#373535' }}>
                      {preVoucher.study_year} — {preVoucher.study_step}
                    </span>
                  </div>
                  {/* 24/09: no celular a dica desce para baixo da série (antes sobrepunha o texto) */}
                  <span className="v2-vgrade__hint" style={{ flex: 'none', maxWidth: '120px', fontSize: '12px', lineHeight: '1.4', color: '#6B6664', textAlign: 'right' }}>
                    Confira se é a série do aluno antes de continuar.
                  </span>
                </div>
                <div style={{ marginTop: '10px', padding: '14px 16px', borderRadius: '12px', background: '#FAF7F5', border: '1px solid #EFE7E3' }}>
                  <p style={{ margin: 0, fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '15px', lineHeight: '1.35', color: '#373535' }}>{String(preVoucher.school_name || '').replace(/[.\s]+$/, '')}</p>
                  <p style={{ margin: '4px 0 0', fontSize: '13px', lineHeight: '1.5', color: '#6B6664' }}>{preVoucher.school_address}</p>
                </div>

                <h2 style={{ margin: '24px 0 0', fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '24px', lineHeight: '1.25', color: '#373535' }}>Quem vai usar os produtos?</h2>
                <label htmlFor="v2-student-name" style={{ ...label, margin: '22px 0 8px' }}>Nome completo do aluno</label>
                <input
                  id="v2-student-name"
                  className="v2d-input"
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Digite o nome como está na matrícula"
                  autoComplete="off"
                  style={inputBase}
                />
                <label htmlFor="v2-student-birthdate" style={{ ...label, margin: '20px 0 8px' }}>Data de nascimento</label>
                <InputMask
                  id="v2-student-birthdate"
                  className="v2d-input"
                  mask="99/99/9999"
                  maskChar=""
                  placeholder="DD/MM/AAAA"
                  inputMode="numeric"
                  autoComplete="off"
                  value={birthdate}
                  onChange={e => setBirthdate(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && _handleBuy()}
                  style={inputBase}
                />
                <p id="v2-student-hint" style={{ margin: '10px 0 0', fontSize: '13px', lineHeight: '1.5', color: step2Hint ? '#C2262B' : '#8E8783' }}>
                  {/* Discovery: orientar ANTES do erro — nome e sobrenome (V-19) */}
                  {step2Hint || 'Informe nome e sobrenome, como está na matrícula.'}
                </p>
                <button
                  id="v2-buy-button"
                  type="button"
                  onClick={_handleBuy}
                  style={{ marginTop: '22px', width: '100%', height: '54px', border: 'none', borderRadius: '12px', background: '#D8262C', color: '#fff', fontFamily: 'Poppins,sans-serif', fontWeight: '600', fontSize: '16px', cursor: 'pointer' }}
                >
                  Continuar para os produtos
                </button>
                <p style={{ margin: '14px 0 0', textAlign: 'center', fontSize: '13px', lineHeight: '1.5', color: '#6B6664' }}>
                  O pedido será identificado na escola pelo nome do aluno.
                </p>
              </div>
            )}
          </div>
          </div>
        </div>
        <ul className="v2-ticks v2-ticks--mobile">
          {TICKS.map(t => (
            <li key={t}><Tick />{t}</li>
          ))}
        </ul>
      </section>

      {/* COMO FUNCIONA */}
      <section id="como-funciona" className="v2-how">
        <span className="v2-eyebrow">Como funciona</span>
        <h2>Três etapas, do voucher à conclusão do pedido.</h2>
        <div className="v2-how__grid">
          <article className="v2-how__card">
            <div className="v2-how__head"><span className="v2-how__num">1</span><h3>Voucher e aluno</h3></div>
            <div className="v2-how__art">
              <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#D8262C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="2.5" y="5" width="19" height="14" rx="2.5" /><path d="M2.5 10h19M6.5 15h5" />
              </svg>
            </div>
            <p>Insira o número do voucher e confirme o nome do aluno e a turma.</p>
          </article>
          <article className="v2-how__card">
            <div className="v2-how__head"><span className="v2-how__num">2</span><h3>Seleção de produtos</h3></div>
            <div className="v2-how__art">
              <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#D8262C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 4.5h14v15H5zM5 8.5h14M9 12.5h6M9 15.5h4" />
              </svg>
            </div>
            <p>A lista da escola já vem montada. Você revisa, ajusta o que é opcional e segue.</p>
          </article>
          <article className="v2-how__card">
            <div className="v2-how__head"><span className="v2-how__num">3</span><h3>Pagamento e conclusão</h3></div>
            <div className="v2-how__art">
              <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#D8262C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M2.5 6.5h11v10h-11zM13.5 9.5h4l3 3v4h-7z" /><circle cx="7" cy="17.5" r="1.8" /><circle cx="17" cy="17.5" r="1.8" />
              </svg>
            </div>
            <p>Escolha entre as formas de pagamento disponíveis para a sua escola e finalize o pedido.</p>
          </article>
        </div>
      </section>

      {/* FAIXA INSTITUCIONAL — SM Educação endossa (números fornecidos pelo marketing) */}
      <section className="v2-inst">
        <div className="v2-inst__in">
          <div className="v2-inst__copy">
            <img src={logoSmBranco} alt="SM Educação" />
            <h2>O SM Direto é o canal oficial de compra da SM Educação.</h2>
            <p>Nascemos na escola e para a escola. O mesmo material que a sua escola adotou, comprado direto de quem publica.</p>
          </div>
          <div className="v2-inst__stats">
            <div><strong>85</strong><span>anos de história</span></div>
            <div><strong>10</strong><span>países</span></div>
            <div><strong>7,1 mi</strong><span>de alunos impactados</span></div>
            <div><strong>18,5 mi</strong><span>de livros por ano no Brasil</span></div>
          </div>
        </div>
      </section>

      {/* DÚVIDAS — mesma fonte do V1 (FaqList/questions.js) */}
      <section id="ajuda" className="v2-faq2 v2-faq-section">
        <div className="v2-faq2__in">
          <aside className="v2-faq2__intro">
            <span className="v2-eyebrow">Dúvidas frequentes</span>
            <h2>Todas as dúvidas, em um só lugar.</h2>
            <div className="v2-faq2__help">
              <img src={carritoConfuso} alt="" aria-hidden="true" />
              <div>
                <p>As perguntas que mais chegam ao nosso atendimento, organizadas por assunto.</p>
                <strong>Não encontrou sua dúvida?</strong>
                <p>Nosso assistente responde na hora e, se precisar, ajuda você a abrir um chamado.</p>
                <button type="button" id="v2-faq-assistant" className="v2-linkbtn" onClick={() => openAssistant()}>Falar com a SM</button>
              </div>
            </div>
          </aside>

          <div className="v2-faq2__list">
            {faqShown.map((item, i) => {
              const open = openFaq === i
              return (
                <div key={i} className={`v2-faq2__item${open ? ' is-open' : ''}`}>
                  <button type="button" className="v2-faq2__q" aria-expanded={open} onClick={() => setOpenFaq(open ? null : i)}>
                    <span>
                      <small>{item.category}</small>
                      {item.q}
                    </span>
                    <i aria-hidden="true">{open ? '−' : '+'}</i>
                  </button>
                  {open && (
                    // conteúdo estático de primeira parte (FaqList/questions.js, mesmo do V1)
                    // div (não p): algumas respostas trazem lista (passo a passo, modelos de entrega)
                    <div className="v2-faq2__a" dangerouslySetInnerHTML={{ __html: item.a }} />
                  )}
                </div>
              )
            })}
            {FAQ_ITEMS.length > FAQ_PREVIEW && (
              <button type="button" className="v2-linkbtn" id="v2-faq-all" onClick={() => setAllFaq(v => !v)}>
                {allFaq ? 'Ver menos' : 'Ver todas as dúvidas'}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* ATENDIMENTO — o botão abre o assistente (que leva ao chamado quando não resolve) */}
      <section className="v2-helpband">
        <div className="v2-helpband__in">
          <span className="v2-helpband__icon" aria-hidden="true">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#D8262C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 14v-2a8 8 0 0 1 16 0v2M4 14a2 2 0 0 1 2-2h1v6H6a2 2 0 0 1-2-2zM20 14a2 2 0 0 0-2-2h-1v6h1a2 2 0 0 0 2-2zM18 18v1a2 2 0 0 1-2 2h-3" />
            </svg>
          </span>
          <div className="v2-helpband__copy">
            <h2>Ficou com dúvida? A gente resolve.</h2>
            {/* 10/10: sem 0800 nem horário (o canal é o chamado — revisão GCA) */}
            <p>Nosso assistente responde na hora e, se precisar, ajuda você a abrir um chamado. Tenha o número do pedido ou do voucher em mãos.</p>
          </div>
          <div className="v2-helpband__actions">
            <button type="button" className="v2-btn v2-btn--primary" id="v2-help-assistant" onClick={() => openAssistant()}>Falar com o assistente</button>
          </div>
        </div>
      </section>
    </V2Layout>
  )
}
